import { useEffect, useId, useMemo, useState, useSyncExternalStore } from 'react'
import { AlertTriangle, ExternalLink, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  NODE_W, NODE_H,
  TONE_CARD, TONE_CHIP, TONE_HEX,
  edgePath, edgeWidth, fmtEta, fmtNum, layoutGraph, particleSpec, toneOf,
} from './flowGraph'
import type { FlowEdgeSpec, FlowNodeSpec, Placed } from './flowGraph'

/**
 * Animated DAG canvas for pipeline topology.
 *
 * Nodes are plain HTML cards absolutely positioned over an SVG edge layer, so
 * text stays crisp and clickable while edges get real bezier curves. Edge
 * thickness encodes throughput (log scale) and colour encodes lag health;
 * particles travelling the path are the actual message flow — their count and
 * speed both scale with throughput, so a busy lane visibly runs hotter than a
 * quiet one and a stalled lane goes dashed and still.
 */

// ─── Hooks ────────────────────────────────────────────────────────────────────

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function subscribeReducedMotion(onChange: () => void) {
  const mq = window.matchMedia(REDUCED_MOTION_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

function useReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  )
}

/** Re-renders once a second so ETA countdowns tick between 30s data refreshes. */
function useTicker(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!enabled) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [enabled])
  return now
}

// ─── Node card ────────────────────────────────────────────────────────────────

function NodeCard({
  placed,
  liveEta,
  dim,
  focused,
  animate,
  onSelectTrace,
}: {
  placed: Placed
  liveEta: number | null | undefined
  dim: boolean
  focused: boolean
  animate: boolean
  onSelectTrace: (n: FlowNodeSpec) => void
}) {
  const n = placed.node
  const tone = toneOf(n.status)
  const failed = tone === 'bad'
  const hasRate = n.ratePerMin != null && n.ratePerMin >= 0
  const hasLag = n.lag != null && n.lag > 0
  const caughtUp = n.lag === 0 && hasRate
  const clickable = Boolean(n.onClick) || Boolean(n.errorTrace)

  if (n.kind === 'placeholder') {
    return (
      <div
        className={cn(
          'absolute border border-dashed border-gray-300 rounded-lg px-3 py-2 bg-gray-50/80 transition-opacity duration-200',
          dim && 'opacity-30'
        )}
        style={{ left: placed.x, top: placed.y, width: NODE_W, height: NODE_H }}
      >
        <span className="text-xs font-mono text-gray-400 block truncate">{n.label}</span>
        <p className="text-[10px] text-gray-300 mt-1">not monitored yet</p>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'absolute border rounded-lg px-2.5 py-1.5 shadow-sm transition-all duration-200 overflow-hidden',
        TONE_CARD[tone],
        dim && 'opacity-35',
        focused && 'shadow-md ring-2 ring-blue-300 ring-offset-1',
        failed && animate && 'animate-alert-pulse',
        clickable && 'cursor-pointer hover:shadow-md group'
      )}
      style={{ left: placed.x, top: placed.y, width: NODE_W, height: NODE_H }}
      onClick={() => (n.errorTrace ? onSelectTrace(n) : n.onClick?.())}
    >
      <div className="flex items-center justify-between gap-1.5">
        <span
          className={cn(
            'text-xs font-mono font-medium text-gray-900 truncate',
            n.onClick && 'group-hover:text-blue-600'
          )}
          title={n.label}
        >
          {n.label}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {failed && <AlertTriangle className="h-3 w-3 text-red-500" />}
          {n.onClick && (
            <ExternalLink className="h-3 w-3 text-gray-300 group-hover:text-blue-400" />
          )}
          {n.status && (
            <span className={cn('text-[9px] px-1 py-0.5 rounded font-medium', TONE_CHIP[tone])}>
              {n.status}
            </span>
          )}
        </div>
      </div>

      {n.sublabel && <p className="text-[9px] text-gray-400 truncate">{n.sublabel}</p>}

      {hasRate && (
        <p className="text-[10px] text-gray-500 mt-0.5 tabular-nums">
          {fmtNum(n.ratePerMin! / 60)}/s <span className="text-gray-300">·</span>{' '}
          {fmtNum(n.ratePerMin!)}/m
        </p>
      )}

      {hasLag && (
        <p className="text-[10px] text-orange-600 mt-0.5 tabular-nums truncate">
          lag {fmtNum(n.lag!)}
          <span className="text-gray-400 ml-1.5">· {fmtEta(liveEta)}</span>
        </p>
      )}

      {caughtUp && !hasLag && <p className="text-[10px] text-green-600 mt-0.5">caught up</p>}
    </div>
  )
}

// ─── Canvas ───────────────────────────────────────────────────────────────────

export function FlowCanvas({
  nodes,
  edges,
  asOfMs,
  paused = false,
  className,
}: {
  nodes: FlowNodeSpec[]
  edges: FlowEdgeSpec[]
  /** Timestamp the metrics were captured — anchors the live ETA countdown. */
  asOfMs: number
  paused?: boolean
  className?: string
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const reduced = useReducedMotion()
  const animate = !paused && !reduced

  const [hoverId, setHoverId] = useState<string | null>(null)
  const [trace, setTrace] = useState<FlowNodeSpec | null>(null)

  const { placed, pos, width, height } = useMemo(() => layoutGraph(nodes), [nodes])

  const hasCountdown = nodes.some(n => n.etaSec != null && n.etaSec > 0)
  const now = useTicker(hasCountdown && !paused)
  const elapsedSec = Math.max(0, Math.floor((now - asOfMs) / 1000))

  /** Nodes one hop from the hovered node — everything else dims. */
  const adjacent = useMemo(() => {
    if (!hoverId) return null
    const set = new Set<string>([hoverId])
    for (const e of edges) {
      if (e.from === hoverId) set.add(e.to)
      if (e.to === hoverId) set.add(e.from)
    }
    return set
  }, [hoverId, edges])

  const resolved = useMemo(
    () =>
      edges.flatMap(e => {
        const s = pos.get(e.from)
        const t = pos.get(e.to)
        if (!s || !t) return []
        const rate = e.ratePerMin ?? t.node.ratePerMin
        const status = e.status ?? t.node.status
        return [{
          key: `${e.from}->${e.to}`,
          d: edgePath(s, t),
          rate,
          tone: toneOf(status),
          label: e.label ?? `${e.from} → ${e.to}`,
          active: hoverId == null || e.from === hoverId || e.to === hoverId,
        }]
      }),
    [edges, pos, hoverId]
  )

  return (
    <div className={cn('w-full', className)}>
      <div className="overflow-x-auto pb-1">
        <div className="relative" style={{ width, height, minWidth: width }}>
          <svg
            width={width}
            height={height}
            className="absolute inset-0"
            style={{ pointerEvents: 'none' }}
            aria-hidden="true"
          >
            {/* Path geometry, referenced by both the visible stroke and the particles. */}
            <defs>
              {resolved.map(e => (
                <path key={e.key} id={`${uid}-${e.key}`} d={e.d} fill="none" />
              ))}
            </defs>

            {resolved.map(e => {
              const hex = TONE_HEX[e.tone]
              const w = edgeWidth(e.rate)
              const blocked = e.tone === 'bad' || e.rate == null || e.rate <= 0
              const { count, dur } = particleSpec(e.rate)

              return (
                <g
                  key={e.key}
                  opacity={e.active ? 1 : 0.12}
                  style={{ transition: 'opacity 200ms' }}
                >
                  <use
                    href={`#${uid}-${e.key}`}
                    stroke={hex}
                    strokeWidth={w}
                    strokeOpacity={blocked ? 0.5 : 0.3}
                    strokeLinecap="round"
                    strokeDasharray={blocked ? '6 6' : undefined}
                    fill="none"
                  />

                  {/* Hit area for the native tooltip — the SVG is pointer-events:none otherwise. */}
                  <use
                    href={`#${uid}-${e.key}`}
                    stroke="transparent"
                    strokeWidth={Math.max(14, w + 10)}
                    fill="none"
                    style={{ pointerEvents: 'stroke' }}
                  >
                    <title>
                      {e.label}
                      {e.rate != null && e.rate >= 0 ? ` — ${fmtNum(e.rate)}/m` : ''}
                    </title>
                  </use>

                  {animate && !blocked
                    ? Array.from({ length: count }, (_, i) => (
                        <circle key={i} r={Math.min(3.5, 1.6 + w * 0.28)} fill={hex}>
                          <animateMotion
                            dur={`${dur}s`}
                            begin={`${((i * dur) / count).toFixed(2)}s`}
                            repeatCount="indefinite"
                            calcMode="linear"
                          >
                            <mpath href={`#${uid}-${e.key}`} />
                          </animateMotion>
                        </circle>
                      ))
                    : null}
                </g>
              )
            })}
          </svg>

          {placed.map(p => (
            <div
              key={p.node.id}
              onMouseEnter={() => setHoverId(p.node.id)}
              onMouseLeave={() => setHoverId(null)}
            >
              <NodeCard
                placed={p}
                liveEta={
                  p.node.etaSec == null ? p.node.etaSec : Math.max(0, p.node.etaSec - elapsedSec)
                }
                dim={adjacent != null && !adjacent.has(p.node.id)}
                focused={hoverId === p.node.id}
                animate={animate}
                onSelectTrace={setTrace}
              />
            </div>
          ))}
        </div>
      </div>

      {trace?.errorTrace && (
        <div className="mt-3 border border-red-200 bg-red-50 rounded-lg p-3">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs font-semibold text-red-700 font-mono">{trace.label}</p>
            <button
              onClick={() => setTrace(null)}
              className="text-red-400 hover:text-red-600 shrink-0"
              aria-label="Close error trace"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <pre className="text-[10px] text-red-600 mt-2 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
            {trace.errorTrace}
          </pre>
        </div>
      )}
    </div>
  )
}

// ─── Legend ───────────────────────────────────────────────────────────────────

export function FlowLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[10px] text-gray-400">
      <span className="flex items-center gap-1.5">
        <svg width="34" height="8" aria-hidden="true">
          <line x1="1" y1="4" x2="33" y2="4" stroke="#94a3b8" strokeWidth="6" strokeOpacity="0.18" strokeLinecap="round" />
          <line x1="1" y1="4" x2="33" y2="4" stroke="#94a3b8" strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" />
        </svg>
        thickness = throughput
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> healthy
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> lagging
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> stalled / failed
      </span>
      <span>dashed = no flow</span>
      <span>hover a node to isolate its lane · click a failed node for its trace</span>
    </div>
  )
}
